import { useCallback, useEffect, useMemo, useState } from "react";
import { useSession } from "@descope/react-sdk";
import { buildTenantGroups } from "./organizationGroups.js";
export { buildTenantGroups } from "./organizationGroups.js";
// Only a recognized Tickaroo host may originate an authenticated platform-API request. Never derive
// the origin from an arbitrary (possibly customer-owned) custom-domain hostname — otherwise the
// browser could send the Descope bearer token to a host we don't control. Returns the Tickaroo public
// domain, or null on a custom-domain host (then the switcher skips the cross-origin calls entirely).
// Strict host match — substring checks like `includes("localhost")` are exploitable
// (`evil-localhost.com` would pass), so a custom domain can never masquerade as a trusted host.
function isLocalhost(host) {
    return host === "localhost" || host === "127.0.0.1" || host === "[::1]" || host.endsWith(".localhost");
}
function tickarooPlatformDomain() {
    const host = window.location.hostname;
    if (isLocalhost(host))
        return "tickaroo-staging.com";
    if (host === "tickaroo.com" || host.endsWith(".tickaroo.com"))
        return "tickaroo.com";
    if (host === "tickaroo-staging.com" || host.endsWith(".tickaroo-staging.com"))
        return "tickaroo-staging.com";
    return null;
}
/** Gatekeeper is served from the Tickaroo `www.<domain>` host; its wildcard CORS permits this. */
function gatekeeperMeUrl(domain) {
    return `https://www.${domain}/api/gatekeeper/v1/me/organizations`;
}
/**
 * The management "my organizations" listing on the Tickaroo apps apex. Same-origin on the apex
 * (identical to what /me does today); a CORS-allowed cross-origin call from a tenant subdomain; relative on localhost so the dev gateway proxy handles it (preserving local /me).
 */
function managementMeUrl(domain) {
    if (isLocalhost(window.location.hostname))
        return "/management/api/me/organizations";
    return `https://apps.${domain}/management/api/me/organizations`;
}
async function fetchJson(url, token, signal) {
    const response = await fetch(url, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
        signal,
    });
    if (!response.ok)
        throw new Error(`Request failed (HTTP ${response.status})`);
    return (await response.json());
}
/**
 * Loads the authenticated caller's tenant groups (liveblog + nxt-cms) for the topbar switcher and the
 * /me launchpad. Each source fetches independently, so a failure or slow load of one never blanks the
 * other; `groups` is rebuilt from whatever has loaded.
 */
export function useMemberOrganizationGroups() {
    const { sessionToken } = useSession();
    const [lb, setLb] = useState({
        status: "loading",
        data: null,
        error: null,
    });
    const [nxt, setNxt] = useState({
        status: "loading",
        data: null,
        error: null,
    });
    const [lbReloadKey, setLbReloadKey] = useState(0);
    const [nxtReloadKey, setNxtReloadKey] = useState(0);
    const refetchLiveblog = useCallback(() => setLbReloadKey((key) => key + 1), []);
    const refetchNxt = useCallback(() => setNxtReloadKey((key) => key + 1), []);
    useEffect(() => {
        // Clear the previous account's data immediately on any session change (and on logout), so a
        // logout/login or token refresh can never transiently render the prior user's organizations.
        if (!sessionToken) {
            setLb({ status: "loading", data: null, error: null });
            return;
        }
        const domain = tickarooPlatformDomain();
        if (!domain) {
            // Custom-domain host: the platform origin is unknown, so we must not send the bearer token
            // off-origin. Settle without fetching; the switcher shows the current-org fallback.
            setLb({ status: "unavailable", data: null, error: null });
            return;
        }
        const controller = new AbortController();
        setLb({ status: "loading", data: null, error: null });
        fetchJson(gatekeeperMeUrl(domain), sessionToken, controller.signal)
            .then((data) => setLb({ status: "success", data, error: null }))
            .catch((err) => {
            if (controller.signal.aborted)
                return;
            setLb({ status: "error", data: null, error: err instanceof Error ? err : new Error("Failed to load liveblog organizations") });
        });
        return () => controller.abort();
    }, [sessionToken, lbReloadKey]);
    useEffect(() => {
        if (!sessionToken) {
            setNxt({ status: "loading", data: null, error: null });
            return;
        }
        const domain = tickarooPlatformDomain();
        if (!domain) {
            setNxt({ status: "unavailable", data: null, error: null });
            return;
        }
        const controller = new AbortController();
        setNxt({ status: "loading", data: null, error: null });
        fetchJson(managementMeUrl(domain), sessionToken, controller.signal)
            .then((data) => setNxt({ status: "success", data, error: null }))
            .catch((err) => {
            if (controller.signal.aborted)
                return;
            setNxt({ status: "error", data: null, error: err instanceof Error ? err : new Error("Failed to load nxt-cms organizations") });
        });
        return () => controller.abort();
    }, [sessionToken, nxtReloadKey]);
    const groups = useMemo(() => buildTenantGroups(lb.data?.tenants ?? [], nxt.data?.organizations ?? []), [lb.data, nxt.data]);
    return {
        groups,
        liveblog: { status: lb.status, error: lb.error, refetch: refetchLiveblog },
        nxt: { status: nxt.status, error: nxt.error, refetch: refetchNxt },
    };
}
//# sourceMappingURL=useMemberOrganizationGroups.js.map